import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { getYoutubeVideoId, isYoutubeShort } from '../../utils/youtube';
import { stripWrappingQuotes, OPEN_QUOTE, CLOSE_QUOTE } from '../../utils/text';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiStar, 
  FiMessageSquare, 
  FiGlobe, 
  FiYoutube, 
  FiShield, 
  FiCheckCircle, 
  FiAlertTriangle,
  FiExternalLink
} from 'react-icons/fi';

const normalizeExternalUrl = (url) => {
  if (!url) return '';
  // Ensure an absolute https URL whose host is prefixed with www.
  let out = url.trim().replace(/^https?:\/\//i, '');
  if (!/^www\./i.test(out)) out = `www.${out}`;
  return `https://${out}`;
};

const byNewest = (a, b) => new Date(b.date || 0) - new Date(a.date || 0);

const sourceFirst = (list, src) =>
  [...list].sort((a, b) =>
    ((b.source?.toLowerCase() === src) - (a.source?.toLowerCase() === src)) || byNewest(a, b)
  );

// Sequencing for the Wall of Love, per the owner's page_review_order setting.
const orderReviews = (reviews, order) => {
  const r = [...reviews];
  switch (order) {
    case 'oldest':
      return r.sort((a, b) => byNewest(b, a));
    case 'random': {
      for (let i = r.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [r[i], r[j]] = [r[j], r[i]];
      }
      return r;
    }
    case 'highest':
      return r.sort((a, b) => ((b.rating || 0) - (a.rating || 0)) || byNewest(a, b));
    case 'detailed':
      return r.sort((a, b) => ((b.content?.length || 0) - (a.content?.length || 0)));
    case 'video_first':
      return r.sort((a, b) => (!!b.youtubeLink - !!a.youtubeLink) || byNewest(a, b));
    case 'platform_mix': {
      // Round-robin across sources so no platform dominates the top.
      const groups = {};
      r.sort(byNewest).forEach(x => {
        const key = x.source || 'Custom';
        (groups[key] = groups[key] || []).push(x);
      });
      const keys = Object.keys(groups);
      const out = [];
      let added = true;
      while (added) {
        added = false;
        for (const k of keys) {
          if (groups[k].length) { out.push(groups[k].shift()); added = true; }
        }
      }
      return out;
    }
    case 'google_first': return sourceFirst(r, 'google');
    case 'trustpilot_first': return sourceFirst(r, 'trustpilot');
    case 'clutch_first': return sourceFirst(r, 'clutch');
    case 'newest':
    default:
      return r.sort(byNewest);
  }
};

const ReviewCard = ({ review }) => {
  const isVideo = review.source?.toLowerCase() === 'youtube' || !!review.youtubeLink;
  const videoId = isVideo ? getYoutubeVideoId(review.youtubeLink) : null;
  const isShort = isVideo ? isYoutubeShort(review.youtubeLink) : false;
  const isPureVideo = isVideo && (!review.content || review.content.trim() === '');

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      // Huge top margin: anything at or above the viewport counts as seen, so
      // jump-scrolling (anchors, restored positions) can't strand cards at
      // opacity 0. Cards below the fold still reveal on scroll.
      viewport={{ once: true, margin: '10000px 0px 0px 0px' }}
      className="flex flex-col rounded-2xl bg-zinc-900/40 backdrop-blur-xl border border-orange-500/10 hover:border-orange-500/30 hover:shadow-[0_8px_30px_rgba(234,88,12,0.08)] transition-all duration-300 overflow-hidden group h-full break-inside-avoid"
    >
      {videoId && (
        <div className="w-full flex-shrink-0 border-b border-white/5 relative z-0">
          <LiteYouTubeEmbed 
            id={videoId} 
            title="YouTube video testimonial" 
            wrapperClass={`yt-lite ${isShort ? 'yt-short' : ''}`}
            noCookie={true}
            params="rel=0&modestbranding=1"
          />
          <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-md rounded-md text-xs font-medium text-zinc-300 border border-white/10 flex items-center gap-1.5 pointer-events-none z-10">
            <SafeIcon icon={FiYoutube} className="text-orange-500" /> YouTube
          </div>
        </div>
      )}

      {!isPureVideo && (
        <div className="p-6 pb-4 flex-grow relative">
          <div className="flex justify-between items-start mb-4">
            <div className="flex gap-1">
              {[...Array(review.rating || 5)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="text-yellow-500 fill-yellow-500 text-sm" />
              ))}
            </div>
          </div>
          <p className="text-zinc-200 leading-relaxed font-medium">{OPEN_QUOTE}{stripWrappingQuotes(review.content)}{CLOSE_QUOTE}</p>
        </div>
      )}

      <div className="p-6 pt-4 mt-auto border-t border-white/5 flex items-center gap-3 bg-zinc-950/50">
        {/* Concurrent Avatars */}
        <div className="flex items-center -space-x-2 flex-shrink-0">
          {review.clientAvatarUrl ? (
            <div className="w-10 h-10 rounded-full border-2 border-zinc-900 bg-zinc-800 relative z-20 overflow-hidden shadow-sm">
              <img src={review.clientAvatarUrl} alt={review.authorName} className="w-full h-full object-cover" />
            </div>
          ) : (!review.companyLogo && (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border-2 border-zinc-900 flex items-center justify-center relative z-20 shadow-sm">
              <span className="text-amber-100 font-semibold">{review.authorName?.charAt(0) || 'A'}</span>
            </div>
          ))}
          {review.companyLogo && (
            <div className="w-10 h-10 rounded-full border-2 border-zinc-900 bg-white relative z-10 overflow-hidden shadow-sm">
              <img src={review.companyLogo} alt={review.companyName} className="w-full h-full object-contain p-0.5" />
            </div>
          )}
        </div>

        <div className="overflow-hidden flex-1 pl-1">
          <div className="text-amber-50 font-medium text-sm truncate">
            {review.authorName || review.companyName || 'Anonymous'}
          </div>
          <div className="text-zinc-500 text-xs truncate flex items-center gap-1.5">
            {review.authorRole && <span>{review.authorRole}</span>}
            {review.authorRole && review.companyName && <span>•</span>}
            {review.companyName && <span className="font-medium text-zinc-400">{review.companyName}</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ReviewsLayout = ({ layout, reviews, spotIdx }) => {
  // Round-robin distribution used by the column-based layouts.
  const cols = (n) => [...Array(n)].map((_, c) => reviews.filter((_, i) => i % n === c));

  switch (layout) {
    case 'grid':
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {reviews.map(t => <ReviewCard key={t.id} review={t} />)}
        </div>
      );

    case 'carousel':
      return (
        <div className="flex gap-6 overflow-x-auto pb-6 snap-x scrollbar-hide">
          {reviews.map(t => (
            <div key={t.id} className="w-[85vw] sm:w-[380px] snap-center flex-shrink-0">
              <ReviewCard review={t} />
            </div>
          ))}
        </div>
      );

    case 'wall': {
      // Endless upward drift, one speed per column; content doubled for a
      // seamless loop. Hover pauses so videos stay clickable.
      const wallCols = cols(3);
      return (
        <div className="relative h-[85vh] overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0a0500] to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0a0500] to-transparent z-10 pointer-events-none" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start h-full">
            {wallCols.map((col, ci) => (
              <div key={ci} className="mot-wall-col flex flex-col gap-6"
                style={{ animation: `mot-wall-scroll ${45 + ci * 12}s linear infinite` }}>
                {[...col, ...col].map((t, i) => <ReviewCard key={`${t.id}-${i}`} review={t} />)}
              </div>
            ))}
          </div>
          <style>{`
            @keyframes mot-wall-scroll { from { transform: translateY(0); } to { transform: translateY(-50%); } }
            .mot-wall-col:hover { animation-play-state: paused !important; }
          `}</style>
        </div>
      );
    }

    case 'timeline':
      return (
        <div className="relative max-w-4xl mx-auto">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-orange-500/50 via-orange-500/20 to-transparent" />
          <div className="space-y-10">
            {reviews.map((t, i) => (
              <div key={t.id} className={`relative flex ${i % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`}>
                <div className="absolute left-4 md:left-1/2 top-6 w-3 h-3 -translate-x-1/2 rounded-full bg-orange-500 border-2 border-[#0a0500] shadow-[0_0_10px_rgba(234,88,12,0.5)]" />
                <div className="w-full pl-10 md:pl-0 md:w-[45%]">
                  <div className={`text-xs text-zinc-500 mb-2 ${i % 2 === 0 ? 'md:text-right md:pr-2' : 'md:pl-2'}`}>
                    {t.date ? new Date(t.date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : ''}
                  </div>
                  <ReviewCard review={t} />
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'spotlight': {
      const featured = reviews[spotIdx % reviews.length];
      const rest = reviews.filter(t => t.id !== featured.id);
      return (
        <div>
          <motion.div key={featured.id}
            initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-12">
            <ReviewCard review={featured} />
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch opacity-80">
            {rest.map(t => <ReviewCard key={t.id} review={t} />)}
          </div>
        </div>
      );
    }

    case 'bento':
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 grid-flow-dense items-stretch">
          {reviews.map((t, i) => (
            <div key={t.id} className={i % 5 === 0 ? 'md:col-span-2' : 'md:col-span-1'}>
              <ReviewCard review={t} />
            </div>
          ))}
        </div>
      );

    case 'marquee': {
      const half = Math.ceil(reviews.length / 2);
      const rows = [reviews.slice(0, half), reviews.slice(half)].filter(r => r.length);
      return (
        <div className="space-y-8 overflow-hidden">
          {rows.map((row, ri) => (
            <div key={ri} className="overflow-hidden">
              <div className="mot-marquee-row flex gap-6 w-max"
                style={{ animation: `mot-marquee ${Math.max(30, row.length * 10)}s linear infinite ${ri % 2 ? 'reverse' : 'normal'}` }}>
                {[...row, ...row].map((t, i) => (
                  <div key={`${t.id}-${i}`} className="w-[320px] sm:w-[380px] flex-shrink-0">
                    <ReviewCard review={t} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <style>{`
            @keyframes mot-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
            .mot-marquee-row:hover { animation-play-state: paused; }
          `}</style>
        </div>
      );
    }

    case 'polaroid':
      return (
        <div className="flex flex-wrap justify-center gap-8">
          {reviews.map((t, i) => (
            <div key={t.id}
              className="w-full sm:w-[320px] transition-transform duration-300 hover:rotate-0 hover:scale-[1.03] hover:z-10"
              style={{ rotate: `${[-3, 2, -1.5, 2.5, -2, 1.5][i % 6]}deg` }}>
              <ReviewCard review={t} />
            </div>
          ))}
        </div>
      );

    case 'chat':
      return (
        <div className="max-w-2xl mx-auto flex flex-col gap-6">
          {reviews.map((t, i) => (
            <div key={t.id} className={`w-[90%] sm:w-[80%] ${i % 2 === 0 ? 'self-start' : 'self-end'}`}>
              <ReviewCard review={t} />
            </div>
          ))}
        </div>
      );

    case 'magazine': {
      const [lead, ...rest] = reviews;
      return (
        <div>
          <div className="max-w-3xl mx-auto text-center mb-16">
            {lead.content && (
              <blockquote className="font-serif text-2xl md:text-4xl leading-snug text-amber-50/95 mb-6">
                {OPEN_QUOTE}{stripWrappingQuotes(lead.content)}{CLOSE_QUOTE}
              </blockquote>
            )}
            <div className="text-sm text-zinc-400">
              — {lead.authorName}{lead.companyName ? `, ${lead.companyName}` : ''}
            </div>
            {lead.youtubeLink && (
              <div className="max-w-xl mx-auto mt-8"><ReviewCard review={lead} /></div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch max-w-5xl mx-auto">
            {rest.map(t => <ReviewCard key={t.id} review={t} />)}
          </div>
        </div>
      );
    }

    case 'masonry':
    default: {
      const [c1, c2, c3] = cols(3);
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
          <div className="flex flex-col gap-6">{c1.map(t => <ReviewCard key={t.id} review={t} />)}</div>
          <div className="flex flex-col gap-6">{c2.map(t => <ReviewCard key={t.id} review={t} />)}</div>
          <div className="flex flex-col gap-6">{c3.map(t => <ReviewCard key={t.id} review={t} />)}</div>
        </div>
      );
    }
  }
};

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [seo, setSeo] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Server-decided (free tier => true). Defaults to showing branding so an
  // unexpected response shape can never hide it for a free page.
  const [showBranding, setShowBranding] = useState(true);

  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const { data: pageData, error: rpcError } = await supabase.rpc('get_public_profile_page', { 
          p_public_id: username 
        });
        
        if (rpcError || !pageData) throw new Error('Profile not found.');

        setProfile(pageData.profile);
        setSeo(pageData.seo);
        setShowBranding(pageData.show_branding !== false);

        if (pageData.reviews) {
          setReviews(pageData.reviews.map(r => ({
            id: r.id,
            source: r.source || 'Custom',
            authorName: r.client_name || 'Anonymous',
            authorRole: r.job_role || '',
            companyName: r.company_name || '',
            companyLogo: r.company_logo_url || '',
            clientAvatarUrl: r.client_avatar_url || '',
            youtubeLink: r.youtube_link || '',
            content: r.content || '',
            rating: r.rating || 5,
            // Prefer the real platform publish date; fall back to import time
            // when a source didn't provide one. Drives Timeline + date orders.
            date: r.source_published_at || r.created_at
          })));
        }

        // Apply SEO Meta Tags to the <head>
        const companyName = pageData.seo?.company_name || pageData.profile?.full_name || username;
        const pageTitle = pageData.seo?.meta_title || `${companyName} Reviews & Testimonials | Moment of Truth`;
        const pageDesc = pageData.seo?.meta_description || `Explore real reviews and testimonials from ${companyName} customers. A simple, transparent Wall of Love built from genuine client feedback.`;
        
        document.title = pageTitle;
        
        const updateMetaTag = (name, content, isProperty = false) => {
          if (!content) return;
          const attr = isProperty ? 'property' : 'name';
          let tag = document.querySelector(`meta[${attr}="${name}"]`);
          if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute(attr, name);
            document.head.appendChild(tag);
          }
          tag.setAttribute('content', content);
        };

        updateMetaTag('description', pageDesc);
        updateMetaTag('og:title', pageTitle, true);
        updateMetaTag('og:description', pageDesc, true);
        if (pageData.seo?.og_image_url) {
          updateMetaTag('og:image', pageData.seo.og_image_url, true);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicData();
  }, [username]);

  // Ordered once per load; random shuffles on page load, not on every render.
  const orderedReviews = useMemo(() => {
    const layout = seo?.page_layout || 'masonry';
    // Timeline is chronological by definition — it overrides the order setting.
    const order = layout === 'timeline' ? 'newest' : (seo?.page_review_order || 'newest');
    return orderReviews(reviews, order);
  }, [reviews, seo]);

  // Spotlight layout rotates its featured review.
  const [spotIdx, setSpotIdx] = useState(0);
  useEffect(() => {
    if ((seo?.page_layout || 'masonry') !== 'spotlight' || orderedReviews.length < 2) return;
    const t = setInterval(() => setSpotIdx(i => (i + 1) % orderedReviews.length), 6000);
    return () => clearInterval(t);
  }, [seo?.page_layout, orderedReviews.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0500] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0500] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
          <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-2xl" />
        </div>
        <h2 className="text-2xl font-bold text-amber-50 mb-2">Page Not Found</h2>
        <p className="text-zinc-400 max-w-md mx-auto leading-relaxed">
          The public page you're looking for doesn't exist or has been disabled.
        </p>
      </div>
    );
  }

  const hasBlueBadge = profile?.has_verified_business || false;
  const hasGreenBadge = profile?.has_verified_reviews || false;
  const hasGoldBadge = profile?.is_pro || false;
  const displayName = profile.full_name || username.charAt(0).toUpperCase() + username.slice(1).replace(/-/g, ' ');

  const pageLayout = seo?.page_layout || 'masonry';

  return (
    <div className="min-h-screen bg-[#0a0500] text-zinc-200 selection:bg-orange-500/30 selection:text-amber-100 relative">
      {/* Background Effects */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-600/10 blur-[120px] pointer-events-none"></div>
      
      <div className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        {/* Header */}
        <header className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-24 h-24 mx-auto bg-zinc-900 border border-orange-500/30 rounded-3xl flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(234,88,12,0.15)] overflow-hidden bg-white/5 backdrop-blur-md"
          >
            {seo?.company_logo_url ? (
              <img src={seo.company_logo_url} alt={seo?.company_name || displayName} className="w-full h-full object-contain p-2" />
            ) : profile?.avatar_url ? (
              <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <SafeIcon icon={FiGlobe} className="text-4xl text-orange-400" />
            )}
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-orange-200 to-amber-50 mb-4"
          >
            {seo?.company_name || displayName}
          </motion.h1>

          {seo?.company_url && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
              className="flex justify-center mb-6"
            >
              <a 
                href={normalizeExternalUrl(seo.company_url)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/50 border border-zinc-800 text-zinc-300 hover:text-amber-100 hover:border-orange-500/30 hover:bg-orange-500/10 transition-all text-sm font-medium shadow-sm"
              >
                <SafeIcon icon={FiExternalLink} className="text-orange-400" /> Visit {seo.company_name || 'Website'}
              </a>
            </motion.div>
          )}

          {/* Badges and Verification Status */}
          {hasBlueBadge ? (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="flex flex-wrap items-center justify-center gap-3 mt-4 mb-6"
            >
              <div className="group relative flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.1)] text-xs font-medium cursor-help">
                <SafeIcon icon={FiShield} /> Verified Business
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[250px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 p-2.5 text-center rounded-lg bg-zinc-900/95 backdrop-blur-md border border-white/10 shadow-xl text-[11px] text-zinc-300 leading-relaxed whitespace-normal">
                  This company has securely verified their domain ownership with Moment of Truth.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-zinc-900/95"></div>
                </div>
              </div>
              
              {hasGreenBadge && (
                <div className="group relative flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.1)] text-xs font-medium cursor-help">
                  <SafeIcon icon={FiCheckCircle} /> Verified Reviews
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[250px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 p-2.5 text-center rounded-lg bg-zinc-900/95 backdrop-blur-md border border-white/10 shadow-xl text-[11px] text-zinc-300 leading-relaxed whitespace-normal">
                    Every review on this page is automatically reviewed by enhanced AI / humans.
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-zinc-900/95"></div>
                  </div>
                </div>
              )}

              {hasGoldBadge && (
                <div className="group relative flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.1)] text-xs font-medium cursor-help">
                  <SafeIcon icon={FiStar} /> Committed to Truth
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[250px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 p-2.5 text-center rounded-lg bg-zinc-900/95 backdrop-blur-md border border-white/10 shadow-xl text-[11px] text-zinc-300 leading-relaxed whitespace-normal">
                    This company actively invests in maintaining a transparent, unalterable record of their customer feedback.
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-zinc-900/95"></div>
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-6 mb-8 mx-auto max-w-md p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3 text-left shadow-[0_0_20px_rgba(239,68,68,0.1)]"
            >
              <SafeIcon icon={FiAlertTriangle} className="text-red-400 mt-0.5 flex-shrink-0 text-lg" />
              <p className="text-sm text-red-200/90 font-medium leading-relaxed">
                The user of this page has not verified that they own this business.
              </p>
            </motion.div>
          )}

        </header>

        {/* Reviews — layout chosen by the page owner (seo.page_layout) */}
        {orderedReviews.length > 0 ? (
          <ReviewsLayout layout={pageLayout} reviews={orderedReviews} spotIdx={spotIdx} />
        ) : (
          <div className="text-center py-20 text-zinc-500">
            No testimonials have been published yet.
          </div>
        )}

        {/* Footer — free tier only; beta/pro pages are unbranded */}
        {showBranding && (
          <footer className="mt-24 pt-8 border-t border-orange-500/10 text-center">
            <a href="https://momentoftruth.io" target="_blank" rel="noopener" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-orange-400 transition-colors">
              Powered by
              <img src="https://whatastory.b-cdn.net/tools/momentoftruth/logo.svg" alt="Logo" className="h-4 opacity-50" />
              <span className="font-semibold text-zinc-400">Moment of Truth</span>
            </a>
          </footer>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;